import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { DoctorIntelligenceDetailResponse } from "@/api/intelligence";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceVariance } from "./IntelligenceVariance";
import { DoctorPerformanceChart } from "./DoctorPerformanceChart";

type Props = {
  detail: DoctorIntelligenceDetailResponse;
};

export function DoctorDetailPanel({ detail }: Props) {
  const c = detail.current;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{detail.doctor.name}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {[detail.doctor.specialisation, detail.doctor.departmentName].filter(Boolean).join(" · ") ||
              "Doctor"}
            {" · "}
            {detail.period.label}
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="text-xl font-semibold tabular-nums">{formatInr(c.revenue)}</p>
              <IntelligenceVariance percent={c.revenueVariancePct} trend={c.trend} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Patients</p>
              <p className="text-xl font-semibold tabular-nums">{formatCount(c.patientVolume)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">OPD Visits</p>
              <p className="text-xl font-semibold tabular-nums">{formatCount(c.opdVisits)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">OPD→IPD</p>
              <p className="text-xl font-semibold tabular-nums">
                {c.opdToIpdConversionPct != null ? `${c.opdToIpdConversionPct.toFixed(1)}%` : "—"}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {c.opdPatientsForConversion > 0
                  ? `${formatCount(c.opdPatientsConvertedToIpd)} of ${formatCount(c.opdPatientsForConversion)} OPD patients`
                  : "No billed OPD patients"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Surgeries</p>
              <p className="text-xl font-semibold tabular-nums">{formatCount(c.surgeries)}</p>
            </div>
          </div>

          {detail.statements.length > 0 && (
            <ul className="mt-4 space-y-1 text-sm text-slate-700 border-t pt-4">
              {detail.statements.map((s) => (
                <li key={s}>• {s}</li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <DoctorPerformanceChart revenueTrend={detail.revenueTrend} />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Service mix (revenue)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          {(
            [
              ["OPD", detail.serviceMix.opd],
              ["Additional Services", detail.serviceMix.additionalServices ?? 0],
              ["IPD", detail.serviceMix.ipd],
              ["Lab", detail.serviceMix.lab],
              ["Pharma", detail.serviceMix.pharma],
              ["Day Care", detail.serviceMix.daycare],
            ] as const
          ).map(([label, val]) => (
            <div key={label} className="rounded-lg border p-3">
              <p className="text-muted-foreground text-xs">{label}</p>
              <p className="font-semibold tabular-nums">{formatInr(val)}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {detail.topPatients.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Top patients by revenue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.topPatients.map((p) => (
              <div key={p.patientId} className="flex justify-between text-sm border-b pb-2 last:border-0">
                <span>
                  {p.patientName}
                  {p.uhid ? ` (${p.uhid})` : ""}
                </span>
                <span className="font-medium tabular-nums">{formatInr(p.totalRevenue)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
